import GlobeIcon from "@shared/svg/globe-icon.svg?react";
import React from "react";

export const EXTERNAL_ICON_SIZES = {
  X_LARGE: "xLarge",
  LARGE: "large",
  MEDIUM: "medium",
  SMALL: "small",
  X_SMALL: "xSmall",
  /** 12×12 badge on thumbnails / avatars */
  ACCORDION: "accordion",
} as const;

export type ExternalIconSizeType =
  (typeof EXTERNAL_ICON_SIZES)[keyof typeof EXTERNAL_ICON_SIZES];

const SIZE_STYLES: Record<ExternalIconSizeType, string> = {
  [EXTERNAL_ICON_SIZES.X_LARGE]: "w-2xl h-2xl rounded-md",
  [EXTERNAL_ICON_SIZES.LARGE]: "w-xl h-xl rounded",
  [EXTERNAL_ICON_SIZES.MEDIUM]: "w-lg h-lg rounded",
  [EXTERNAL_ICON_SIZES.SMALL]: "w-md h-md rounded-sm",
  [EXTERNAL_ICON_SIZES.X_SMALL]: "w-sm h-sm rounded-sm",
  [EXTERNAL_ICON_SIZES.ACCORDION]: "w-[12px] h-[12px] rounded-[2px]",
};

/** Standard corner offset for external badges on thumbnails */
export const EXTERNAL_ICON_THUMB_BADGE_CLASS =
  "absolute -top-[4px] -right-[4px] pointer-events-none";

export const EXTERNAL_ICON_THUMB_WRAP_CLASS =
  "relative shrink-0 overflow-visible mr-lg";

export const isExternalVisibility = (
  visibilityScope?: string | null,
  isExternalFlag?: boolean | null,
) =>
  Boolean(isExternalFlag) ||
  String(visibilityScope || "").toLowerCase() === "external";

const getIconSize = (size: ExternalIconSizeType) => {
  switch (size) {
    case EXTERNAL_ICON_SIZES.X_LARGE:
      return 16;
    case EXTERNAL_ICON_SIZES.LARGE:
      return 14;
    case EXTERNAL_ICON_SIZES.MEDIUM:
      return 12;
    case EXTERNAL_ICON_SIZES.SMALL:
      return 8;
    case EXTERNAL_ICON_SIZES.X_SMALL:
      return 6;
    case EXTERNAL_ICON_SIZES.ACCORDION:
      return 10;
  }
};

type ExternalIconPropsType = {
  size?: ExternalIconSizeType;
  className?: string;
};

const ExternalIcon = ({
  size = EXTERNAL_ICON_SIZES.X_LARGE,
  className = "",
}: ExternalIconPropsType) => (
  <div
    className={`flex items-center justify-center bg-dark ${SIZE_STYLES[size]} ${className}`}
  >
    <GlobeIcon
      width={getIconSize(size)}
      height={getIconSize(size)}
      className="text-inverse no-icon-styles"
    />
  </div>
);

type ExternalThumbnailFrameProps = {
  children: React.ReactNode;
  showExternal?: boolean;
  className?: string;
};

/** Relative wrapper for a thumbnail/avatar plus optional external badge */
export const ExternalThumbnailFrame = ({
  children,
  showExternal,
  className = "w-2xl h-2xl",
}: ExternalThumbnailFrameProps) => (
  <div className={`${EXTERNAL_ICON_THUMB_WRAP_CLASS} ${className}`}>
    {children}
    {showExternal ? (
      <ExternalIcon
        size={EXTERNAL_ICON_SIZES.ACCORDION}
        className={EXTERNAL_ICON_THUMB_BADGE_CLASS}
      />
    ) : null}
  </div>
);

export default ExternalIcon;
